"use client";

import { RiArrowRightSLine, RiShoppingBag3Line } from "react-icons/ri";
import { Badge, Button, Skeleton } from "@dionatanbig2be/big2be-ui";
import { OrderStatusIcon } from "./OrderStatusIcon";
import { OrderItemThumb, ExtraBadge } from "./OrderItemThumb";
import {
    formatBRL,
    getRefundLabel,
    getStageLabel,
    progressIndex,
    PROGRESS_STEPS,
    STATUS_META,
} from "../../lib/orders.config";
import type { OrderCard } from "../../types/orders";

interface OrderCardViewProps {
    order: OrderCard;
    onSelect: (order: OrderCard) => void;
}

/**
 * Card de um pedido na listagem — status com etapa fina, trilha de progresso
 * compacta, miniaturas dos itens (skeleton enquanto hidrata) e total.
 *
 * O card INTEIRO abre os detalhes: um botão invisível estica sobre ele
 * (z-[1]); o botão "Ver detalhes" real fica acima (z-[2]) para manter um alvo
 * visível e o foco de teclado com rótulo próprio.
 */
export function OrderCardView({ order, onSelect }: OrderCardViewProps) {
    const meta = STATUS_META[order.status];
    const stage = getStageLabel(order.rawStatus, order.status);
    const refundLabel = getRefundLabel(order.refundStatus ?? null);
    const cancelled = order.status === "cancelado";

    return (
        <article className="relative flex flex-col overflow-hidden rounded-2xl border border-base-300/60 bg-base-100 transition-colors hover:border-primary/30">
            <button
                type="button"
                onClick={() => onSelect(order)}
                aria-label={`Ver detalhes do pedido ${order.id}`}
                className="absolute inset-0 z-[1] cursor-pointer rounded-2xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary/60"
            />

            <header className="flex items-center gap-3 px-5 pt-4">
                <span
                    aria-hidden="true"
                    className={`flex size-10 shrink-0 items-center justify-center rounded-xl ${meta.disc}`}
                >
                    <OrderStatusIcon status={order.status} size={19} />
                </span>
                <div className="min-w-0 flex-1">
                    <div className={`truncate text-[0.95rem] font-extrabold ${meta.text}`}>{stage}</div>
                    <div className="mt-0.5 truncate text-[0.78rem] font-semibold text-base-content/50">
                        Pedido #{order.id}
                        {order.dateLabel ? ` · ${order.dateLabel}` : ""}
                    </div>
                </div>
                {refundLabel && (
                    <Badge tone="neutral" variant="soft" size="sm" className="shrink-0">
                        {refundLabel}
                    </Badge>
                )}
            </header>

            {!cancelled && <ProgressTrack order={order} />}

            {/* Miniaturas dos itens — skeleton enquanto o card hidrata */}
            <div
                className={`flex min-h-14 items-center gap-2 px-5 pt-4 ${
                    cancelled ? "opacity-45 grayscale" : ""
                }`}
            >
                {!order.hydrated ? (
                    <>
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Skeleton key={i} className="size-14 rounded-xl" />
                        ))}
                    </>
                ) : order.itemsPreview.length > 0 ? (
                    <>
                        {order.itemsPreview.map((item) => (
                            <OrderItemThumb key={item.id} item={item} />
                        ))}
                        {order.extraCount > 0 && <ExtraBadge count={order.extraCount} />}
                    </>
                ) : (
                    <span className="inline-flex items-center gap-2 text-[0.82rem] font-semibold text-base-content/45">
                        <RiShoppingBag3Line size={17} aria-hidden="true" />
                        Abra para ver os itens do pedido
                    </span>
                )}
            </div>

            <footer className="mt-4 flex items-center justify-between gap-3 border-t border-base-300/40 bg-base-200/25 px-5 py-3">
                <div className="flex min-w-0 flex-col">
                    {!order.hydrated ? (
                        <>
                            <Skeleton className="h-5 w-24" />
                            <Skeleton className="mt-1.5 h-3 w-14" />
                        </>
                    ) : (
                        <>
                            <span
                                className={`text-[1.05rem] font-extrabold tabular-nums leading-tight ${
                                    cancelled ? "text-base-content/50 line-through" : "text-base-content"
                                }`}
                            >
                                {order.total !== undefined ? formatBRL(order.total) : "—"}
                            </span>
                            {order.itemCount !== undefined && (
                                <span className="text-[0.75rem] font-semibold text-base-content/45">
                                    {order.itemCount} {order.itemCount === 1 ? "item" : "itens"}
                                </span>
                            )}
                        </>
                    )}
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onSelect(order)}
                    className="relative z-[2] shrink-0 whitespace-nowrap"
                >
                    Ver detalhes <RiArrowRightSLine size={16} aria-hidden="true" />
                </Button>
            </footer>
        </article>
    );
}

/**
 * Trilha de progresso compacta (4 segmentos = etapas do stepper). A próxima
 * etapa pulsa — em warning quando o pedido ainda espera pagamento, no tom do
 * tema quando já está andando. Concluído pinta tudo de success.
 */
function ProgressTrack({ order }: { order: OrderCard }) {
    const stepIdx = progressIndex(order.progressStep);
    const doneColor = order.status === "concluido" ? "bg-success" : "bg-primary";

    return (
        <div className="flex gap-1 px-5 pt-3" aria-hidden="true">
            {PROGRESS_STEPS.map((step, idx) => {
                let color = "bg-base-300/70";
                if (idx <= stepIdx) color = doneColor;
                else if (idx === stepIdx + 1)
                    color =
                        order.status === "aguardando-pagamento"
                            ? "animate-pulse bg-warning/70"
                            : "animate-pulse bg-primary/30";
                return <span key={step.key} className={`h-1 flex-1 rounded-full ${color}`} />;
            })}
        </div>
    );
}
